import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Loader, EmptyState, Modal, Select } from "@/components/ui/Primitives";
import { unwrap } from "@/lib/unwrap";
import {
  FileText, Upload, Download, Share2, Inbox, Send, Trash2,
  Image as ImageIcon, FileSpreadsheet, FileCode2, FileBadge2, X, UploadCloud, Link2, Check,
} from "lucide-react";
import { toast } from "sonner";

// CRA T776 + lease lifecycle aligned categories.
const CATEGORIES = [
  { key: "lease", label: "Lease" },
  { key: "notice", label: "Notice" },
  { key: "insurance", label: "Insurance" },
  { key: "receipt", label: "Receipt" },
  { key: "tax", label: "Tax / CRA" },
  { key: "id", label: "ID / Verification" },
  { key: "other", label: "Other" },
];
const CATEGORY_TONE = {
  lease: "bg-sky-50 text-sky-700 border-sky-200",
  notice: "bg-amber-50 text-amber-700 border-amber-200",
  insurance: "bg-indigo-50 text-indigo-700 border-indigo-200",
  receipt: "bg-emerald-50 text-emerald-700 border-emerald-200",
  tax: "bg-rose-50 text-rose-700 border-rose-200",
  id: "bg-slate-100 text-slate-700 border-slate-200",
  other: "bg-slate-50 text-slate-600 border-slate-200",
};

// Map MIME prefix → tinted icon + tone for the card thumb.
function iconForDoc(d) {
  const mime = (d.mime_type || "").toLowerCase();
  const name = (d.name || d.filename || "").toLowerCase();
  if (mime.startsWith("image/") || /\.(png|jpe?g|webp|gif|heic|heif)$/.test(name)) {
    return { Icon: ImageIcon, tone: "bg-violet-50 text-violet-700" };
  }
  if (mime.includes("pdf") || name.endsWith(".pdf")) {
    return { Icon: FileBadge2, tone: "bg-rose-50 text-rose-700" };
  }
  if (mime.includes("spreadsheet") || /\.(csv|xlsx?|ods)$/.test(name)) {
    return { Icon: FileSpreadsheet, tone: "bg-emerald-50 text-emerald-700" };
  }
  if (mime.includes("text") || /\.(txt|md|json|xml|html?|js|ts|py)$/.test(name)) {
    return { Icon: FileCode2, tone: "bg-sky-50 text-sky-700" };
  }
  return { Icon: FileText, tone: "bg-green-50 text-green-700" };
}

function humanSize(bytes) {
  if (!bytes || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Backend stores the raw base64 length but most listing endpoints
// return either size_bytes / file_size — fall back through them.
function docBytes(d) {
  return d.size_bytes ?? d.file_size ?? d.bytes ?? null;
}

const MAX_BYTES = 20 * 1024 * 1024; // 20 MB cap — base64 inflates by ~33%.

/**
 * CopyLinkButton — one-tap copy of a shareable link for a document.
 * Prefers the doc's public share URL, falls back to the download URL.
 * Shows a Check icon for 1.4s after copy so the user gets clear
 * feedback without a toast (touch-friendly).
 */
function CopyLinkButton({ doc }) {
  const [copied, setCopied] = useState(false);
  const link = doc?.public_url || doc?.share_url || doc?.download_url || doc?.url;
  if (!link) return null;
  return (
    <Button
      variant="ghost"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(link);
          setCopied(true);
          toast.success("Link copied.");
          setTimeout(() => setCopied(false), 1400);
        } catch {
          toast.error("Couldn't copy — long-press the link instead.");
        }
      }}
      className="!px-3 !py-2 text-xs min-h-[40px]"
      data-testid={`doc-copy-link-${doc.document_id}`}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Link2 className="w-3.5 h-3.5" />}
      <span className="hidden sm:inline">{copied ? "Copied" : "Copy link"}</span>
    </Button>
  );
}



export default function Documents() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState([]);
  const [shared, setShared] = useState([]);
  const [sent, setSent] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(params.get("upload") === "1");
  const [stagedFile, setStagedFile] = useState(null);
  const [category, setCategory] = useState("other");
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareTo, setShareTo] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const isTenant = user?.role === "tenant";

  const load = async () => {
    setLoading(true);
    try {
      const [d, sw, sb, k] = await Promise.all([
        api.get("/documents").catch(() => ({ data: [] })),
        api.get("/shared-with-me").catch(() => ({ data: [] })),
        api.get("/shared-by-me").catch(() => ({ data: [] })),
        api.get("/me/chat-contacts").catch(() => ({ data: [] })),
      ]);
      setMine(unwrap(d.data, "documents"));
      setShared(unwrap(sw.data, "documents"));
      setSent(unwrap(sb.data, "documents"));
      setContacts(unwrap(k.data, "contacts"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetUpload = () => {
    setStagedFile(null);
    setProgress(0);
    setCategory("other");
    if (fileRef.current) fileRef.current.value = "";
  };

  const stageFile = (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 20 MB.`);
      return;
    }
    setStagedFile(file);
    // Best-effort category guess from extension/mime.
    const m = (file.type || "").toLowerCase();
    const n = (file.name || "").toLowerCase();
    if (/lease/.test(n)) setCategory("lease");
    else if (/notice|n4|n12|eviction/.test(n)) setCategory("notice");
    else if (/insurance|coi/.test(n)) setCategory("insurance");
    else if (/receipt|rent/.test(n)) setCategory("receipt");
    else if (/t776|t5|tax|nr4|cra/.test(n)) setCategory("tax");
    else if (m.startsWith("image/") && /id|driver|passport/.test(n)) setCategory("id");
    else setCategory("other");
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) stageFile(f);
  }, []);

  const upload = async (e) => {
    e?.preventDefault();
    if (!stagedFile) return toast.error("Pick a file first");
    setBusy(true);
    setProgress(5);
    try {
      // Read straight to ArrayBuffer (skips data-URL overhead),
      // base64-encode in 32 KB chunks with cooperative yields so the UI
      // thread never freezes. Progress updates during encoding (0→55)
      // + upload (55→95) + finalize (→100).
      const buf = await stagedFile.arrayBuffer();
      const bytes = new Uint8Array(buf);
      const CHUNK = 32768;
      let binary = "";
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + CHUNK, bytes.length)));
        if (i % (CHUNK * 16) === 0 && i > 0) {
          await new Promise((r) => setTimeout(r, 0));
          setProgress(5 + Math.round((i / bytes.length) * 50));
        }
      }
      setProgress(55);
      const data_base64 = btoa(binary);
      setProgress(65);
      await api.post("/documents", {
        name: stagedFile.name,
        data_base64,
        mime_type: stagedFile.type || "application/octet-stream",
        category,
      });
      setProgress(100);
      toast.success(`Uploaded ${stagedFile.name}`);
      setUploadOpen(false);
      resetUpload();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? (detail[0]?.msg || "Upload failed")
        : "Upload failed";
      toast.error(msg);
      setProgress(0);
    } finally { setBusy(false); }
  };

  const openShare = (doc) => { setShareTarget(doc); setShareTo(""); setShareOpen(true); };

  const share = async (e) => {
    e.preventDefault();
    if (!shareTarget) return;
    setBusy(true);
    try {
      const endpoint = isTenant ? "/tenant/share-document" : "/landlord/share-document";
      await api.post(endpoint, {
        document_id: shareTarget.document_id,
        recipient_user_id: shareTo,
      });
      toast.success(isTenant ? "Sent to your landlord" : "Document shared");
      setShareOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Share failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/documents/${deleteTarget.document_id}`);
      toast.success("Document deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not delete");
    } finally { setDeleting(false); }
  };

  const download = async (doc) => {
    const id = doc.document_id;
    const filename = doc.name || doc.filename || doc.title || "document";
    const toastId = toast.loading(`Preparing ${filename}…`);
    try {
      const { data } = await api.get(`/documents/${id}`);
      const b64 = data?.data_base64;
      if (!b64) throw new Error("No file data returned");
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: data.mime_type || "application/octet-stream" });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      }, 2000);
      toast.success(`${filename} downloaded`, { id: toastId });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (err?.message || "Download failed");
      toast.error(msg, { id: toastId });
    }
  };

  if (loading) return <Loader/>;

  const tabs = [
    { key: "mine", label: "My documents", count: mine.length, icon: FileText },
    { key: "shared", label: "Shared with me", count: shared.length, icon: Inbox },
    { key: "sent", label: "Sent", count: sent.length, icon: Send },
  ];

  const list = tab === "mine" ? mine : tab === "shared" ? shared : sent;

  return (
    <div>
      <PageHeader
        title="Documents"
        subtitle={isTenant ? "Receive documents from your landlord and send your own." : "Send leases, notices, and certificates to your tenants."}
        actions={
          <Button onClick={() => setUploadOpen(true)} data-testid="upload-doc-button">
            <Upload className="w-4 h-4"/> Upload
          </Button>
        }
      />

      <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`doc-tab-${t.key}`}
            className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors inline-flex items-center gap-2 ${tab === t.key ? "border-green-800 text-slate-950" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <t.icon className="w-4 h-4"/> {t.label} <span className="text-xs text-slate-400">({t.count})</span>
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={tab === "mine" ? "No documents yet" : tab === "shared" ? "Nothing shared with you" : "You haven't sent any docs"}
          description={tab === "mine" ? "Upload PDFs, certificates, or notices to your secure vault." : tab === "shared" ? "When someone shares a document with you, it lands here." : "Share a document from My documents to send it."}
          action={tab === "mine" ? <Button onClick={() => setUploadOpen(true)} data-testid="empty-upload-cta"><Upload className="w-4 h-4"/> Upload your first document</Button> : null}
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((d) => {
            const { Icon, tone } = iconForDoc(d);
            const bytes = docBytes(d);
            const cat = d.category || "other";
            const catTone = CATEGORY_TONE[cat] || CATEGORY_TONE.other;
            // Show a real thumbnail for images so the card gallery feels
            // like a modern file browser (Google Drive / Dropbox), not a
            // Bootstrap list. Falls back to the tinted icon otherwise.
            const isImage = ((d.mime_type || "").startsWith("image/")
              || /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(d.filename || d.name || ""));
            const thumbUrl = isImage ? (d.thumb_url || d.download_url || d.url || d.public_url) : null;
            return (
              <Card
                key={d.document_id}
                className="p-0 overflow-hidden card-lift hover:ring-2 hover:ring-emerald-100 transition"
                data-testid={`doc-${d.document_id}`}
              >
                {/* Thumbnail lane — image preview OR tinted icon block. */}
                <div className={`relative w-full h-32 border-b border-slate-100 ${isImage ? "bg-slate-100" : tone}`}>
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt={d.title || d.name || "Document preview"}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      data-testid={`doc-thumb-${d.document_id}`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon className="w-10 h-10" />
                    </div>
                  )}
                  {/* Category chip floats on the thumb for at-a-glance sorting. */}
                  <span className={`absolute top-2 left-2 inline-flex items-center text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border ${catTone} bg-white/90 backdrop-blur`} data-testid={`doc-cat-${d.document_id}`}>
                    {CATEGORIES.find(c => c.key === cat)?.label || cat}
                  </span>
                </div>
                {/* Meta lane */}
                <div className="p-4">
                  <div className="font-semibold text-slate-900 truncate">{d.title || d.name || d.filename || "Document"}</div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{d.filename || d.mime_type || ""}</div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    {bytes != null && <span className="text-[10px] text-slate-400 tabular-nums" data-testid={`doc-size-${d.document_id}`}>{humanSize(bytes)}</span>}
                    <span className="text-[10px] text-slate-400">·</span>
                    <span className="text-[10px] text-slate-400">{formatDate(d.created_at || d.uploaded_at || d.shared_at)}</span>
                  </div>
                  {/* Action bar — 44px min height for mobile tap targets. */}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
                    <Button variant="secondary" onClick={() => download(d)} className="!px-3 !py-2 text-xs min-h-[40px]" data-testid={`doc-download-${d.document_id}`}>
                      <Download className="w-3.5 h-3.5"/> <span className="hidden sm:inline">Download</span>
                    </Button>
                    <CopyLinkButton doc={d} />
                    {tab === "mine" && (
                      <>
                        <Button variant="ghost" onClick={() => openShare(d)} className="!px-3 !py-2 text-xs min-h-[40px]" data-testid={`doc-share-${d.document_id}`}>
                          <Share2 className="w-3.5 h-3.5"/> <span className="hidden sm:inline">Share</span>
                        </Button>
                        <Button variant="ghost" onClick={() => setDeleteTarget(d)} className="!px-2 !py-2 text-xs text-red-600 hover:bg-red-50 ml-auto min-h-[40px]" data-testid={`doc-delete-${d.document_id}`}>
                          <Trash2 className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete document?" testid="delete-doc-modal">
        <p className="text-sm text-slate-600">
          “{deleteTarget?.title || deleteTarget?.name || deleteTarget?.filename || "This document"}” will be permanently removed. This cannot be undone.
        </p>
        <div className="flex items-center justify-end gap-2 pt-5">
          <Button type="button" variant="ghost" onClick={() => setDeleteTarget(null)} data-testid="delete-doc-cancel">Cancel</Button>
          <Button type="button" onClick={remove} disabled={deleting} className="!bg-red-600 hover:!bg-red-700" data-testid="delete-doc-confirm">
            <Trash2 className="w-4 h-4"/> {deleting ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </Modal>

      <Modal open={uploadOpen} onClose={() => { setUploadOpen(false); resetUpload(); }} title="Upload document" testid="upload-doc-modal">
        <form onSubmit={upload} className="space-y-4">
          {/* Drag-and-drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            data-testid="doc-dropzone"
            className={`relative cursor-pointer rounded-2xl border-2 border-dashed px-5 py-8 text-center transition-colors ${dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"}`}
          >
            <input ref={fileRef} type="file" className="hidden" data-testid="doc-file-input"
              onChange={(e) => stageFile(e.target.files?.[0])} />
            {!stagedFile ? (
              <>
                <UploadCloud className="w-9 h-9 mx-auto text-slate-400"/>
                <div className="mt-2 text-sm font-semibold text-slate-800">Drag &amp; drop a file here</div>
                <div className="mt-1 text-xs text-slate-500">or <span className="text-emerald-700 font-semibold">click to browse</span> · PDF, DOCX, image · up to 20 MB</div>
              </>
            ) : (
              <div className="flex items-center gap-3 text-left">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-none">
                  <FileText className="w-5 h-5"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate" data-testid="doc-staged-name">{stagedFile.name}</div>
                  <div className="text-xs text-slate-500 tabular-nums">{humanSize(stagedFile.size)} · {stagedFile.type || "file"}</div>
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); resetUpload(); }} className="p-1.5 rounded hover:bg-slate-200 text-slate-500" aria-label="Remove file" data-testid="doc-staged-clear">
                  <X className="w-4 h-4"/>
                </button>
              </div>
            )}
          </div>

          {/* Category selector */}
          <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)} testid="doc-category-select">
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </Select>

          {/* Progress bar — only visible mid-upload */}
          {busy && (
            <div className="space-y-1.5" data-testid="doc-upload-progress">
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all duration-200" style={{ width: `${progress}%` }}/>
              </div>
              <div className="text-xs text-slate-500 tabular-nums text-right">{progress}%</div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => { setUploadOpen(false); resetUpload(); }}>Cancel</Button>
            <Button type="submit" disabled={busy || !stagedFile} data-testid="doc-upload-submit">
              {busy ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={shareOpen} onClose={() => setShareOpen(false)} title={`Share "${shareTarget?.title || shareTarget?.filename || ""}"`} testid="share-doc-modal">
        <form onSubmit={share} className="space-y-4">
          {contacts.length === 0 ? (
            <div className="py-4 text-sm text-slate-500 text-center">No recipients available. Once a lease links you to a {isTenant ? "landlord" : "tenant"}, they&apos;ll appear here.</div>
          ) : (
            <Select label="Send to" required value={shareTo} onChange={(e) => setShareTo(e.target.value)} testid="doc-share-to">
              <option value="">Select recipient</option>
              {contacts.map((c) => <option key={c.user_id} value={c.user_id}>{c.name || c.email}</option>)}
            </Select>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShareOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !shareTo} data-testid="doc-share-submit">{busy ? "Sending…" : "Send"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
